import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { canRaisePO, formatINR, hasCapability } from "@/lib/permissions";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import AssetNameSelect, { isCuratedCategory } from "@/components/AssetNameSelect";
import { BrandInput } from "@/components/BrandInput";
import { MultiMachinePicker } from "@/components/MachinePicker";
import { useAssetCategories } from "@/hooks/useAssetCategories";

// Category lists come from useAssetCategories() (Asset Registry master) — Sep 2026.

const CURRENCIES = [
  { code: "INR", label: "₹ INR — Indian Rupee" },
  { code: "USD", label: "$ USD — US Dollar" },
  { code: "EUR", label: "€ EUR — Euro" },
  { code: "CNY", label: "¥ CNY — Chinese Yuan" },
  { code: "JPY", label: "¥ JPY — Japanese Yen" },
];
const CURRENCY_SYMBOL = { INR: "₹", USD: "$", EUR: "€", CNY: "¥", JPY: "¥" };
const GST_TREATMENTS = [
  { code: "", label: "— None —" },
  { code: "IGST", label: "IGST (import/inter-state)" },
  { code: "CGST_SGST", label: "CGST + SGST (intra-state)" },
  { code: "Duty Free", label: "Duty Free" },
  { code: "Nil", label: "Nil-rated / Exempt" },
];

const emptyLine = () => ({
  id: crypto.randomUUID(),
  description: "", qty: 1, uom: "", unit_price: 0,
  gst_percent: 18, hs_code: "", gst_treatment: "",
  consumable_id: "",
});

const emptyCharge = (charge_type = "transportation") => ({
  id: crypto.randomUUID(),
  charge_type,
  label: "",
  amount: 0,
  apply_gst: false,
  gst_percent: 18,
});

const CHARGE_TYPES = [
  { code: "transportation", label: "Transportation / Freight" },
  { code: "installation", label: "Installation / Commissioning" },
  { code: "other", label: "Other Charges" },
];

const fmtMoney = (n, ccy = "INR") => {
  const sym = CURRENCY_SYMBOL[ccy] || "";
  const val = Number(n || 0);
  const opts = ccy === "INR"
    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return `${sym}${val.toLocaleString(ccy === "INR" ? "en-IN" : "en-US", opts)}`;
};

export default function PurchaseOrderForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromRequestId = searchParams.get("from_request");
  const { user } = useAuth();

  const { all: assetCategories, typeOf: assetTypeOf } = useAssetCategories();
  const [form, setForm] = useState({
    po_type: "asset_procurement",
    po_date: new Date().toISOString().slice(0, 10),
    currency: "INR",
    vendor_id: "", vendor_name: "", vendor_gstin: "", vendor_address: "", vendor_email: "", vendor_phone: "",
    delivery_address: "", payment_terms: "Net 30 days",
    bill_to_address_id: "",
    ship_to_address_id: "",
    expected_delivery_date: "",
    notes: "",
    linked_asset_id: "",
    linked_asset_ids: [],
    linked_machine_types: [],
    linked_request_id: "",
    line_items: [emptyLine()],
    discount_type: "flat",
    discount_value: 0,
    additional_charges: [],
    for_new_asset: {
      name: "", category: "Machinery", asset_type: "tangible",
      manufacturer: "", brand: "", model_number: "", location: "",
      useful_life_years: 5, depreciation_method: "SLM", depreciation_rate: 15,
    },
  });
  const [assets, setAssets] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [consumables, setConsumables] = useState([]);
  const [companyAddresses, setCompanyAddresses] = useState([]);
  const [linkedRequest, setLinkedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitAction, setSubmitAction] = useState("draft"); // draft | submit

  useEffect(() => {
    if (!hasCapability(user, "raise_po")) {
      toast.error("You do not have permission to raise POs.");
      navigate("/purchase-orders");
      return;
    }
    api.get("/assets").then(({ data }) => setAssets(data)).catch(() => {});
    api.get("/vendors").then(({ data }) => setVendors(data)).catch(() => {});
    api.get("/consumables").then(({ data }) => setConsumables(data)).catch(() => {});
    api.get("/company-settings").then(({ data }) => {
      const addrs = data.addresses || [];
      setCompanyAddresses(addrs);
      // pre-fill defaults for new PO (not when editing — edit case already has ids)
      if (!isEdit) {
        const defBill = addrs.find((a) => a.is_default_bill_to) || addrs[0];
        const defShip = addrs.find((a) => a.is_default_ship_to) || addrs[0];
        setForm((f) => ({
          ...f,
          bill_to_address_id: defBill?.id || "",
          ship_to_address_id: defShip?.id || "",
        }));
      }
    }).catch(() => {});
    if (isEdit) {
      api.get(`/purchase-orders/${id}`).then(({ data }) => {
        setForm({
          po_type: data.po_type,
          po_date: (data.po_date || "").slice(0, 10),
          currency: data.currency || "INR",
          vendor_name: data.vendor_name || "",
          vendor_gstin: data.vendor_gstin || "",
          vendor_address: data.vendor_address || "",
          vendor_email: data.vendor_email || "",
          vendor_phone: data.vendor_phone || "",
          delivery_address: data.delivery_address || "",
          payment_terms: data.payment_terms || "",
          bill_to_address_id: data.bill_to_address_id || "",
          ship_to_address_id: data.ship_to_address_id || "",
          expected_delivery_date: (data.expected_delivery_date || "").slice(0, 10),
          notes: data.notes || "",
          linked_asset_id: data.linked_asset_id || "",
          linked_asset_ids: data.linked_asset_ids || (data.linked_asset_id ? [data.linked_asset_id] : []),
          linked_machine_types: data.linked_machine_types || [],
          linked_request_id: data.linked_request_id || "",
          line_items: (data.line_items || []).map((l) => ({
            ...l,
            id: l.id || crypto.randomUUID(),
            uom: l.uom || "",
            hs_code: l.hs_code || "",
            gst_treatment: l.gst_treatment || "",
            consumable_id: l.consumable_id || "",
          })),
          discount_type: data.discount_type || "flat",
          discount_value: data.discount_value || 0,
          additional_charges: (data.additional_charges || []).map((c) => ({
            ...c,
            id: c.id || crypto.randomUUID(),
          })),
          for_new_asset: data.for_new_asset || {
            name: "", category: "Machinery", asset_type: "tangible",
            manufacturer: "", brand: "", model_number: "", location: "",
            useful_life_years: 5, depreciation_method: "SLM", depreciation_rate: 15,
          },
        });
      }).catch(() => toast.error("Failed to load PO"));
    } else if (fromRequestId) {
      // Pre-fill from an approved consumable requisition
      api.get(`/consumable-requests/${fromRequestId}`).then(({ data: req }) => {
        setLinkedRequest(req);
        if (req.status !== "approved") {
          toast.warning(`Note: requisition ${req.request_number} is in status "${req.status}"`);
        }
        const lines = (req.items || []).map((it) => ({
          id: crypto.randomUUID(),
          description: `${it.code || ""} ${it.name || ""}`.trim(),
          qty: Number(it.qty_requested) || 1,
          uom: it.unit_of_measure || "",
          unit_price: Number(it.unit_cost_at_request) || 0,
          gst_percent: 18,
          hs_code: "",
          gst_treatment: "",
          consumable_id: it.consumable_id,
        }));
        setForm((f) => ({
          ...f,
          po_type: "consumables_purchase",
          linked_request_id: req.id,
          notes: (req.justification ? `Fulfilling requisition ${req.request_number}: ${req.justification}` : `Fulfilling requisition ${req.request_number}`),
          line_items: lines.length ? lines : f.line_items,
        }));
      }).catch(() => toast.error("Failed to load requisition"));
    }
    // eslint-disable-next-line
  }, [id, fromRequestId]);

  // When editing a legacy PO whose line_items were stored without a UOM,
  // back-fill each line's UOM from the linked consumable master as soon as
  // both the PO and the consumables list have loaded.
  useEffect(() => {
    if (!consumables.length) return;
    setForm((f) => {
      let changed = false;
      const next = f.line_items.map((l) => {
        if (l.consumable_id && !(l.uom || "").trim()) {
          const c = consumables.find((x) => x.id === l.consumable_id);
          if (c && (c.unit_of_measure || "").trim()) {
            changed = true;
            return { ...l, uom: c.unit_of_measure };
          }
        }
        return l;
      });
      return changed ? { ...f, line_items: next } : f;
    });
  }, [consumables]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (parent, k, v) => setForm((f) => ({ ...f, [parent]: { ...f[parent], [k]: v } }));

  const updateLine = (lineId, field, value) => {
    setForm((f) => ({
      ...f,
      line_items: f.line_items.map((l) => l.id === lineId ? { ...l, [field]: value } : l),
    }));
  };
  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, emptyLine()] }));
  const removeLine = (lineId) => setForm((f) => ({
    ...f,
    line_items: f.line_items.length > 1 ? f.line_items.filter((l) => l.id !== lineId) : f.line_items,
  }));

  // ----- Consumable master picker (only relevant for consumables_purchase POs) -----
  const pickConsumableForLine = (lineId, consId) => {
    const c = consumables.find((x) => x.id === consId);
    setForm((f) => ({
      ...f,
      line_items: f.line_items.map((l) => {
        if (l.id !== lineId) return l;
        if (!c) return { ...l, consumable_id: "" };
        return {
          ...l,
          consumable_id: c.id,
          description: `${c.code || ""} ${c.name || ""}`.trim(),
          uom: l.uom || c.unit_of_measure || "",
          unit_price: Number(l.unit_price) || Number(c.unit_cost) || 0,
        };
      }),
    }));
  };

  // ----- Additional charges (transportation / installation / other) -----
  const addCharge = (charge_type) =>
    setForm((f) => ({ ...f, additional_charges: [...f.additional_charges, emptyCharge(charge_type)] }));
  const updateCharge = (id, field, value) =>
    setForm((f) => ({
      ...f,
      additional_charges: f.additional_charges.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
    }));
  const removeCharge = (id) =>
    setForm((f) => ({ ...f, additional_charges: f.additional_charges.filter((c) => c.id !== id) }));

  const totals = useMemo(() => {
    let subtotal = 0;
    form.line_items.forEach((l) => {
      subtotal += (Number(l.qty) || 0) * (Number(l.unit_price) || 0);
    });
    // Discount reduces the pre-GST base
    const dRaw = Number(form.discount_value) || 0;
    let discount_amount = form.discount_type === "percent" ? (subtotal * dRaw) / 100 : dRaw;
    discount_amount = Math.max(0, Math.min(discount_amount, subtotal));
    const taxable_base = subtotal - discount_amount;
    // Per-line GST after proportional discount allocation
    let line_gst = 0;
    form.line_items.forEach((l) => {
      const base = (Number(l.qty) || 0) * (Number(l.unit_price) || 0);
      const share = subtotal > 0 ? base / subtotal : 0;
      const liTax = base - discount_amount * share;
      line_gst += (liTax * (Number(l.gst_percent) || 0)) / 100;
    });
    // Additional charges
    let ac_subtotal = 0;
    let ac_gst = 0;
    form.additional_charges.forEach((c) => {
      const amt = Number(c.amount) || 0;
      ac_subtotal += amt;
      if (c.apply_gst) ac_gst += (amt * (Number(c.gst_percent) || 0)) / 100;
    });
    const total_gst = line_gst + ac_gst;
    const grand_total = taxable_base + total_gst + ac_subtotal;
    return { subtotal, discount_amount, taxable_base, line_gst, ac_subtotal, ac_gst, total_gst, grand_total };
  }, [form.line_items, form.additional_charges, form.discount_type, form.discount_value]);

  const submit = async (action) => {
    if (!form.vendor_name.trim()) {
      toast.error("Vendor name is required");
      return;
    }
    if (form.line_items.length === 0 || !form.line_items.some((l) => l.description && l.qty > 0)) {
      toast.error("Add at least one valid line item");
      return;
    }
    if (form.po_type === "maintenance_spare" && !form.linked_asset_ids.length && !form.linked_machine_types.length) {
      toast.error("Please select at least one machine or machine type this PO relates to");
      return;
    }
    // Note: for_new_asset is intentionally NOT validated at PO creation. If
    // missing, the accounting/stores team is prompted to fill it in during
    // the Post-to-Books step.
    setSubmitting(true);
    setSubmitAction(action);
    try {
      const payload = {
        po_type: form.po_type,
        po_date: form.po_date,
        currency: form.currency || "INR",
        vendor_id: form.vendor_id || null,
        vendor_name: form.vendor_name.trim(),
        vendor_gstin: form.vendor_gstin || null,
        vendor_address: form.vendor_address || null,
        vendor_email: form.vendor_email || null,
        vendor_phone: form.vendor_phone || null,
        delivery_address: form.delivery_address || null,
        payment_terms: form.payment_terms || null,
        expected_delivery_date: form.expected_delivery_date || null,
        bill_to_address_id: form.bill_to_address_id || null,
        ship_to_address_id: form.ship_to_address_id || null,
        notes: form.notes || null,
        linked_asset_id: form.po_type === "maintenance_spare" ? (form.linked_asset_ids[0] || null) : null,
        linked_asset_ids: form.po_type === "maintenance_spare" ? form.linked_asset_ids : null,
        linked_machine_types: form.po_type === "maintenance_spare" ? form.linked_machine_types : null,
        linked_request_id: form.linked_request_id || null,
        for_new_asset: form.po_type === "asset_procurement" && form.for_new_asset.name.trim() ? {
          ...form.for_new_asset,
          asset_type: (assetTypeOf(form.for_new_asset.category) === "intangible" ? "intangible" : "tangible"),
          useful_life_years: Number(form.for_new_asset.useful_life_years) || 5,
          depreciation_rate: Number(form.for_new_asset.depreciation_rate) || 15,
        } : null,
        line_items: form.line_items
          .filter((l) => l.description && Number(l.qty) > 0)
          .map((l) => ({
            description: l.description,
            qty: Number(l.qty),
            uom: (l.uom || "").trim() || null,
            unit_price: Number(l.unit_price) || 0,
            gst_percent: Number(l.gst_percent) || 0,
            hs_code: (l.hs_code || "").trim() || null,
            gst_treatment: l.gst_treatment || null,
            consumable_id: l.consumable_id || null,
          })),
        discount_type: form.discount_type || "flat",
        discount_value: Number(form.discount_value) || 0,
        additional_charges: form.additional_charges
          .filter((c) => Number(c.amount) > 0)
          .map((c) => ({
            charge_type: c.charge_type,
            label: (c.label || "").trim() || null,
            amount: Number(c.amount),
            apply_gst: !!c.apply_gst,
            gst_percent: c.apply_gst ? (Number(c.gst_percent) || 0) : 0,
          })),
      };

      let saved;
      if (isEdit) {
        const { data } = await api.patch(`/purchase-orders/${id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/purchase-orders", payload);
        saved = data;
      }
      if (action === "submit") {
        await api.post(`/purchase-orders/${saved.id}/submit`);
        toast.success("PO submitted for approval");
      } else {
        toast.success(isEdit ? "PO updated" : "PO saved as draft");
      }
      navigate(`/purchase-orders/${saved.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save PO");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="po-form-page">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div>
        <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">
          {isEdit ? "Edit" : "New"} Purchase Order
        </div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">
          {isEdit ? "Update PO" : "Raise a Purchase Order"}
        </h1>
      </div>

      {/* Linked Requisition banner */}
      {linkedRequest && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 flex items-start gap-3" data-testid="linked-request-banner">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">CR</div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-indigo-900">
              Raising PO against requisition <span className="font-mono">{linkedRequest.request_number}</span>
            </div>
            <div className="text-xs text-indigo-700 mt-0.5">
              Requested by {linkedRequest.requested_by_name} · {(linkedRequest.items || []).length} item(s) · When you save this PO, the requisition status will move to <b>PO Raised</b>. On mark-received, stock and ledger update automatically.
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/consumable-requests/${linkedRequest.id}`)}
            className="text-xs text-indigo-700 underline hover:text-indigo-900"
          >
            View requisition
          </button>
        </div>
      )}

      {/* PO Type & Meta */}
      <Card>
        <CardHeader><CardTitle className="font-heading text-base">PO Type & Basic Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6">
          <div>
            <Label>PO Type *</Label>
            <Select value={form.po_type} onValueChange={(v) => set("po_type", v)}>
              <SelectTrigger data-testid="po-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="asset_procurement">Fixed Asset (Capital Goods) — CG</SelectItem>
                <SelectItem value="maintenance_spare">Machine Spares & Attachments / Jigs — SPR</SelectItem>
                <SelectItem value="consumables_purchase">Consumables — CON</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>PO Date *</Label>
            <Input required type="date" value={form.po_date} onChange={(e) => set("po_date", e.target.value)} />
          </div>
          <div>
            <Label>Currency *</Label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger data-testid="po-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.currency !== "INR" && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1.5 leading-snug">
                FCY PO — exchange rate will be captured at inward (BOE / bank advice). INR asset value = Base × Rate + GST on INR base.
              </div>
            )}
          </div>
          {form.po_type === "maintenance_spare" && (
            <div className="md:col-span-3">
              <Label>Linked Machines / Machine Types *</Label>
              <MultiMachinePicker
                assets={assets.filter((a) => a.category !== "Attachments and Jigs")}
                ids={form.linked_asset_ids}
                types={form.linked_machine_types}
                onChange={({ ids, types }) => setForm((f) => ({ ...f, linked_asset_ids: ids, linked_machine_types: types }))}
                testid="po-linked-asset"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Pick specific machines, or "All &lt;machine&gt;" for a common spare. The exact machine and line are recorded later when the spare is issued.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vendor */}
      <Card>
        <CardHeader><CardTitle className="font-heading text-base">Vendor Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
          <div className="md:col-span-2">
            <Label>Vendor *</Label>
            <div className="flex gap-2 items-start">
              <select
                value={form.vendor_id}
                onChange={(e) => {
                  const vid = e.target.value;
                  const v = vendors.find((x) => x.id === vid);
                  if (v) {
                    setForm(f => ({
                      ...f,
                      vendor_id: v.id,
                      vendor_name: v.name || f.vendor_name,
                      vendor_gstin: v.gstin || "",
                      vendor_address: v.address || "",
                      vendor_email: v.email || "",
                      vendor_phone: v.phone || "",
                      payment_terms: v.credit_terms || f.payment_terms,
                    }));
                  } else {
                    set("vendor_id", "");
                  }
                }}
                className="flex-1 h-10 rounded-md border border-slate-200 px-2 text-sm"
                data-testid="po-vendor-picker"
              >
                <option value="">— Select from vendor master —</option>
                {(() => {
                  const catForType = {
                    asset_procurement: "Machinery",
                    maintenance_spare: "Spares & Jigs",
                    consumables_purchase: "Consumables",
                  }[form.po_type];
                  const matched = [];
                  const uncategorized = [];
                  const other = [];
                  vendors.forEach((v) => {
                    const cats = v.categories || (v.category ? [v.category] : []);
                    if (cats.length === 0) uncategorized.push(v);
                    else if (catForType && cats.includes(catForType)) matched.push(v);
                    else other.push(v);
                  });
                  const chip = (cats) => (cats && cats.length ? ` · ${cats.slice(0, 2).join(", ")}${cats.length > 2 ? "…" : ""}` : "");
                  return (
                    <>
                      {matched.length > 0 && (
                        <optgroup label={`Recommended for ${catForType || "this PO"} (${matched.length})`}>
                          {matched.map((v) => (
                            <option key={v.id} value={v.id}>{v.name}{chip(v.categories)}</option>
                          ))}
                        </optgroup>
                      )}
                      {uncategorized.length > 0 && (
                        <optgroup label={`Uncategorized (${uncategorized.length})`}>
                          {uncategorized.map((v) => (
                            <option key={v.id} value={v.id}>{v.name}</option>
                          ))}
                        </optgroup>
                      )}
                      {other.length > 0 && (
                        <optgroup label={`Other vendors (${other.length})`}>
                          {other.map((v) => (
                            <option key={v.id} value={v.id}>{v.name}{chip(v.categories)}</option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  );
                })()}
              </select>
              <a
                href="/vendors"
                target="_blank"
                rel="noreferrer"
                className="text-xs text-slate-500 hover:text-[#7C1D3A] pt-2.5 whitespace-nowrap"
              >
                + Add new
              </a>
            </div>
            <div className="mt-2">
              <Input
                required
                value={form.vendor_name}
                onChange={(e) => set("vendor_name", e.target.value)}
                data-testid="po-vendor-name"
                placeholder="Vendor name (auto-filled from picker; can be overridden)"
              />
            </div>
          </div>
          <div><Label>Vendor GSTIN</Label><Input value={form.vendor_gstin} onChange={(e) => set("vendor_gstin", e.target.value)} placeholder="27AAAAA0000A1Z5" /></div>
          <div><Label>Vendor Email</Label><Input type="email" value={form.vendor_email} onChange={(e) => set("vendor_email", e.target.value)} /></div>
          <div><Label>Vendor Phone</Label><Input value={form.vendor_phone} onChange={(e) => set("vendor_phone", e.target.value)} /></div>
          <div><Label>Expected Delivery Date</Label><Input type="date" value={form.expected_delivery_date} onChange={(e) => set("expected_delivery_date", e.target.value)} /></div>
          <div className="md:col-span-2">
            <Label>Vendor Address</Label>
            <Textarea rows={2} value={form.vendor_address} onChange={(e) => set("vendor_address", e.target.value)} />
          </div>
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-base">
            Line Items <span className="text-xs font-normal text-slate-500 ml-1">· all values in {form.currency}</span>
          </CardTitle>
          <Button type="button" onClick={addLine} size="sm" variant="outline" data-testid="add-line-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Line
          </Button>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          <div className="hidden md:grid grid-cols-12 gap-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-2">
            <div className="col-span-3">Description</div>
            <div className="col-span-1">HS Code</div>
            <div className="col-span-1 text-right">Qty</div>
            <div className="col-span-1">UOM</div>
            <div className="col-span-2 text-right">Unit ({CURRENCY_SYMBOL[form.currency]})</div>
            <div className="col-span-1 text-right">GST %</div>
            <div className="col-span-2">Treatment</div>
            <div className="col-span-1 text-right">Total</div>
          </div>
          {form.line_items.map((l, idx) => {
            const base = (Number(l.qty) || 0) * (Number(l.unit_price) || 0);
            const gst = base * (Number(l.gst_percent) || 0) / 100;
            const total = base + gst;
            return (
              <div key={l.id} className="grid grid-cols-12 gap-2 items-start p-2 bg-slate-50 rounded-md" data-testid={`po-line-${idx}`}>
                {form.po_type === "consumables_purchase" && (
                  <div className="col-span-12">
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Pick from Consumable Master (auto-fills description & unit cost)</Label>
                    <select
                      value={l.consumable_id || ""}
                      onChange={(e) => pickConsumableForLine(l.id, e.target.value)}
                      className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm mt-1"
                      data-testid={`po-line-${idx}-consumable`}
                    >
                      <option value="">— Ad-hoc (no ledger link) —</option>
                      {consumables.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.code} · {c.name} — stock {c.current_stock ?? 0} {c.unit_of_measure}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="col-span-12 md:col-span-3">
                  <Textarea rows={2} placeholder="Item / description — press Enter for component lists (1. … 2. …)" value={l.description} onChange={(e) => updateLine(l.id, "description", e.target.value)} data-testid={`po-line-${idx}-desc`} />
                </div>
                <div className="col-span-6 md:col-span-1">
                  <Input placeholder="HSN/HS" value={l.hs_code || ""} onChange={(e) => updateLine(l.id, "hs_code", e.target.value)} data-testid={`po-line-${idx}-hs`} />
                </div>
                <div className="col-span-3 md:col-span-1">
                  <Input type="number" min="0" step="0.01" value={l.qty} onChange={(e) => updateLine(l.id, "qty", e.target.value)} className="text-right" data-testid={`po-line-${idx}-qty`} />
                </div>
                <div className="col-span-3 md:col-span-1">
                  <Input placeholder="Pcs" value={l.uom || ""} onChange={(e) => updateLine(l.id, "uom", e.target.value)} data-testid={`po-line-${idx}-uom`} />
                </div>
                <div className="col-span-3 md:col-span-2">
                  <Input type="number" min="0" step="0.01" value={l.unit_price} onChange={(e) => updateLine(l.id, "unit_price", e.target.value)} className="text-right" data-testid={`po-line-${idx}-price`} />
                </div>
                <div className="col-span-3 md:col-span-1">
                  <Input type="number" min="0" step="0.1" value={l.gst_percent} onChange={(e) => updateLine(l.id, "gst_percent", e.target.value)} className="text-right" data-testid={`po-line-${idx}-gst`} />
                </div>
                <div className="col-span-6 md:col-span-2">
                  <select
                    value={l.gst_treatment || ""}
                    onChange={(e) => updateLine(l.id, "gst_treatment", e.target.value)}
                    className="w-full h-10 rounded-md border border-slate-200 px-2 text-xs"
                    data-testid={`po-line-${idx}-treatment`}
                  >
                    {GST_TREATMENTS.map((t) => (
                      <option key={t.code} value={t.code}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-3 md:col-span-1 text-right font-mono text-sm py-2 text-slate-700">{fmtMoney(total, form.currency)}</div>
                <div className="col-span-12 flex justify-end">
                  <button type="button" onClick={() => removeLine(l.id)} className="text-red-500 hover:text-red-700 p-1 text-xs" disabled={form.line_items.length === 1}>
                    <Trash2 className="w-3.5 h-3.5 inline mr-1" /> Remove line
                  </button>
                </div>
              </div>
            );
          })}

          {/* Discount + Additional Charges */}
          <div className="border-t border-slate-200 pt-4 mt-4 space-y-3" data-testid="po-charges-block">
            <div className="flex flex-wrap items-end gap-3">
              <div className="text-sm font-semibold text-slate-700 mr-4">Discount (pre-GST)</div>
              <div>
                <Label className="text-[10px] text-slate-500 uppercase">Type</Label>
                <select
                  value={form.discount_type}
                  onChange={(e) => set("discount_type", e.target.value)}
                  className="h-9 rounded-md border border-slate-200 px-2 text-sm block"
                  data-testid="po-discount-type"
                >
                  <option value="flat">Flat amount ({CURRENCY_SYMBOL[form.currency]})</option>
                  <option value="percent">Percentage (%)</option>
                </select>
              </div>
              <div>
                <Label className="text-[10px] text-slate-500 uppercase">Value</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={form.discount_value}
                  onChange={(e) => set("discount_value", e.target.value)}
                  className="w-32 text-right"
                  data-testid="po-discount-value"
                />
              </div>
              <div className="text-xs text-slate-500 pb-2">
                = <span className="font-mono text-red-600">-{fmtMoney(totals.discount_amount, form.currency)}</span> reduces the taxable base
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-slate-700">Additional Charges</div>
                <div className="flex gap-2">
                  {CHARGE_TYPES.map((ct) => (
                    <Button
                      key={ct.code}
                      type="button"
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      onClick={() => addCharge(ct.code)}
                      data-testid={`add-charge-${ct.code}`}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> {ct.label}
                    </Button>
                  ))}
                </div>
              </div>
              {form.additional_charges.length === 0 && (
                <div className="text-xs text-slate-500 italic py-2">
                  No additional charges yet. Add Transportation / Installation / Other charges above — each can independently choose whether GST applies.
                </div>
              )}
              {form.additional_charges.map((ch, cidx) => {
                const amt = Number(ch.amount) || 0;
                const g = ch.apply_gst ? (amt * (Number(ch.gst_percent) || 0)) / 100 : 0;
                return (
                  <div key={ch.id} className="grid grid-cols-12 gap-2 items-center p-2 bg-amber-50/60 border border-amber-100 rounded-md mb-2" data-testid={`po-charge-${cidx}`}>
                    <div className="col-span-6 md:col-span-3">
                      <select
                        value={ch.charge_type}
                        onChange={(e) => updateCharge(ch.id, "charge_type", e.target.value)}
                        className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm"
                      >
                        {CHARGE_TYPES.map((t) => (
                          <option key={t.code} value={t.code}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <Input placeholder="Label (e.g. Freight — Blue Dart)" value={ch.label} onChange={(e) => updateCharge(ch.id, "label", e.target.value)} />
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <Input type="number" min="0" step="0.01" value={ch.amount} onChange={(e) => updateCharge(ch.id, "amount", e.target.value)} className="text-right" placeholder="Amount" data-testid={`po-charge-${cidx}-amount`} />
                    </div>
                    <div className="col-span-4 md:col-span-2 flex items-center gap-2">
                      <input
                        id={`gstchk-${ch.id}`}
                        type="checkbox"
                        checked={!!ch.apply_gst}
                        onChange={(e) => updateCharge(ch.id, "apply_gst", e.target.checked)}
                        className="w-4 h-4 accent-[#7C1D3A]"
                        data-testid={`po-charge-${cidx}-gst-toggle`}
                      />
                      <label htmlFor={`gstchk-${ch.id}`} className="text-xs">Apply GST</label>
                      <Input
                        type="number" min="0" step="0.1"
                        disabled={!ch.apply_gst}
                        value={ch.gst_percent}
                        onChange={(e) => updateCharge(ch.id, "gst_percent", e.target.value)}
                        className="w-16 text-right"
                      />
                      <span className="text-xs text-slate-500">%</span>
                    </div>
                    <div className="col-span-3 md:col-span-1 text-right font-mono text-sm text-slate-700">
                      {fmtMoney(amt + g, form.currency)}
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <button type="button" onClick={() => removeCharge(ch.id)} className="text-red-500 hover:text-red-700 p-1" title="Remove">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Totals */}
          <div className="border-t border-slate-200 pt-4 mt-4 space-y-1 text-sm">
            <div className="flex justify-end gap-8"><span className="text-slate-500">Line items subtotal</span><span className="font-mono w-40 text-right">{fmtMoney(totals.subtotal, form.currency)}</span></div>
            {totals.discount_amount > 0 && (
              <>
                <div className="flex justify-end gap-8"><span className="text-slate-500">Less: Discount{form.discount_type === "percent" ? ` (${Number(form.discount_value) || 0}%)` : ""}</span><span className="font-mono w-40 text-right text-red-600">-{fmtMoney(totals.discount_amount, form.currency)}</span></div>
                <div className="flex justify-end gap-8"><span className="text-slate-500">Taxable base</span><span className="font-mono w-40 text-right">{fmtMoney(totals.taxable_base, form.currency)}</span></div>
              </>
            )}
            {totals.ac_subtotal > 0 && (
              <div className="flex justify-end gap-8"><span className="text-slate-500">Additional charges</span><span className="font-mono w-40 text-right">{fmtMoney(totals.ac_subtotal, form.currency)}</span></div>
            )}
            <div className="flex justify-end gap-8"><span className="text-slate-500">Total GST</span><span className="font-mono w-40 text-right">{fmtMoney(totals.total_gst, form.currency)}</span></div>
            <div className="flex justify-end gap-8 pt-2 border-t border-slate-100 font-semibold text-base">
              <span className="text-[#7C1D3A]">Grand Total</span>
              <span className="font-mono w-40 text-right text-[#7C1D3A]" data-testid="po-grand-total">{fmtMoney(totals.grand_total, form.currency)}</span>
            </div>
            {form.currency !== "INR" && (
              <div className="text-[11px] text-slate-500 text-right pt-1">
                INR equivalent will be locked at inward when the BOE exchange rate is captured.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Asset details (only for asset procurement) — OPTIONAL at PO creation */}
      {form.po_type === "asset_procurement" && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-base">
              New Asset Details <span className="text-xs font-normal text-slate-500">· optional at PO creation</span>
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Fill this now to auto-map the asset at receipt. If left blank, the stores/accounts team will be prompted to enter it during <b>Post to Books</b>.
            </p>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
            <div className="md:col-span-2">
              <Label>
                Asset Name
                {isCuratedCategory(form.for_new_asset.category) && (
                  <span className="text-xs font-normal text-slate-500 ml-1">
                    — pick from {form.for_new_asset.category} list
                  </span>
                )}
              </Label>
              <AssetNameSelect
                category={form.for_new_asset.category}
                value={form.for_new_asset.name}
                onChange={(v) => setNested("for_new_asset", "name", v)}
                testid="po-asset-name"
                placeholder="e.g. CNC Laser TruLaser 3030 (leave blank to fill later)"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.for_new_asset.category} onValueChange={(v) => setNested("for_new_asset", "category", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {assetCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Invoicing Party</Label><Input value={form.for_new_asset.manufacturer} onChange={(e) => setNested("for_new_asset", "manufacturer", e.target.value)} data-testid="po-asset-invoicing-party" /></div>
            <div><Label>Brand</Label><BrandInput value={form.for_new_asset.brand || ""} onChange={(v) => setNested("for_new_asset", "brand", v)} testid="po-asset-brand" /></div>
            <div><Label>Model Number</Label><Input value={form.for_new_asset.model_number} onChange={(e) => setNested("for_new_asset", "model_number", e.target.value)} /></div>
            <div><Label>Installation Location</Label><Input value={form.for_new_asset.location} onChange={(e) => setNested("for_new_asset", "location", e.target.value)} /></div>
            <div><Label>Useful Life (years)</Label><Input type="number" value={form.for_new_asset.useful_life_years} onChange={(e) => setNested("for_new_asset", "useful_life_years", e.target.value)} /></div>
            <div>
              <Label>Depreciation Method</Label>
              <Select value={form.for_new_asset.depreciation_method} onValueChange={(v) => setNested("for_new_asset", "depreciation_method", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SLM">Straight-Line</SelectItem>
                  <SelectItem value="WDV">Written-Down Value</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delivery & notes */}
      <Card>
        <CardHeader><CardTitle className="font-heading text-base">Bill-To, Ship-To & Notes</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
          <div>
            <Label>Bill To (invoice recipient) *</Label>
            <select
              value={form.bill_to_address_id}
              onChange={(e) => set("bill_to_address_id", e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm"
              data-testid="po-bill-to-select"
            >
              <option value="">— Select address —</option>
              {companyAddresses.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}{a.is_default_bill_to ? " (default)" : ""}
                </option>
              ))}
            </select>
            {companyAddresses.length === 0 && (
              <div className="text-[11px] text-amber-700 mt-1">No addresses set — <a className="underline" href="/company-settings">add in Company Settings</a>.</div>
            )}
          </div>
          <div>
            <Label>Ship To (delivery premise) *</Label>
            <select
              value={form.ship_to_address_id}
              onChange={(e) => set("ship_to_address_id", e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm"
              data-testid="po-ship-to-select"
            >
              <option value="">— Select address —</option>
              {companyAddresses.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}{a.is_default_ship_to ? " (default)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <Label>Delivery Address override (optional)</Label>
            <Textarea rows={2} value={form.delivery_address} onChange={(e) => set("delivery_address", e.target.value)} placeholder="Leave blank to use the selected Ship-To address" />
          </div>
          <div>
            <Label>Payment Terms</Label>
            <Input value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} placeholder="e.g. Net 30 days" />
          </div>
          <div className="md:col-span-2">
            <Label>Notes / Special Instructions</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3 sticky bottom-4 bg-white/80 backdrop-blur p-3 rounded-md border border-slate-200">
        <Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={submitting}>Cancel</Button>
        <Button type="button" variant="outline" onClick={() => submit("draft")} disabled={submitting} data-testid="save-draft-btn">
          {submitting && submitAction === "draft" ? "Saving..." : "Save as Draft"}
        </Button>
        <Button type="button" onClick={() => submit("submit")} disabled={submitting} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="submit-po-btn">
          {submitting && submitAction === "submit" ? "Submitting..." : "Submit for Approval"}
        </Button>
      </div>
    </div>
  );
}
